import React from "react";
import {
  View,
  TouchableOpacity,
  Text,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { Save, X, ChevronDown, ChevronUp } from "lucide-react";
import Markdown from "react-native-markdown-display";
import { RainbowBorder } from "../../../../../../common/native";
import { NoteSummaryProps } from "../../types/VoiceNotes";

const NoteSummary: React.FC<NoteSummaryProps> = ({
  note,
  editingNoteId,
  editingField,
  editedSummary,
  expandedSummaries,
  saving,
  tipsEnabled,
  tipsState,
  onSaveSummary,
  onCancelEdit,
  onToggleSummary,
  onMarkStepComplete,
  setEditedSummary,
  TOOLTIP_STEPS,
}) => {
  if (editingNoteId === note.id && editingField === "summary") {
    return (
      <View style={styles.editContainer}>
        <TextInput
          style={styles.summaryInput}
          value={editedSummary}
          onChangeText={setEditedSummary}
          multiline
          numberOfLines={8}
          autoCapitalize="sentences"
          blurOnSubmit={false}
        />

        <View style={styles.editActions}>
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={onCancelEdit}
            disabled={saving}
          >
            <X size={16} color="#555555" />
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.saveButton, saving && styles.disabledButton]}
            onPress={() => onSaveSummary(note)}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Save size={16} color="#FFFFFF" />
                <Text style={styles.saveButtonText}>Save</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const fullSummary = note.note || "No summary available";
  const previewSummary =
    fullSummary.length > 150
      ? `${fullSummary.substring(0, 150)}...`
      : fullSummary;

  return (
    <View>
      <View
        style={
          expandedSummaries[note.id]
            ? styles.summaryExpanded
            : styles.summaryPreview
        }
      >
        <Markdown style={markdownStyles}>
          {expandedSummaries[note.id] ? fullSummary : previewSummary}
        </Markdown>
      </View>

      <View style={styles.summaryActionButtons}>
        <RainbowBorder
          isActive={
            tipsEnabled &&
            tipsState.viewTranscript &&
            !tipsState.selectImageToSimulate
          }
          borderRadius={8}
        >
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => {
              onToggleSummary(note.id);
              if (
                tipsEnabled &&
                tipsState.viewTranscript &&
                !tipsState.selectImageToSimulate
              ) {
                onMarkStepComplete(TOOLTIP_STEPS.VIEW_TRANSCRIPT);
              }
            }}
          >
            {expandedSummaries[note.id] ? (
              <ChevronUp size={16} color="#0085FF" />
            ) : (
              <ChevronDown size={16} color="#0085FF" />
            )}
            <Text style={styles.actionButtonText}>
              {expandedSummaries[note.id] ? "Hide" : "Show"}
            </Text>
          </TouchableOpacity>
        </RainbowBorder>
      </View>
    </View>
  );
};

const markdownStyles = StyleSheet.create({
  body: {
    color: "#333333",
    fontSize: 14,
    lineHeight: 20,
  },
  hr: {
    marginTop: 14,
    marginBottom: 14,
    backgroundColor: "#D8DEE8",
    height: 1,
  },
  paragraph: {
    marginTop: 6,
    marginBottom: 6,
  },
  heading2: {
    marginTop: 10,
    marginBottom: 6,
    fontSize: 17,
    fontWeight: "700",
    color: "#202939",
  },
  bullet_list: {
    marginTop: 4,
    marginBottom: 6,
  },
  list_item: {
    marginBottom: 2,
  },
});

const styles = StyleSheet.create({
  editContainer: {
    marginTop: 8,
  },
  summaryPreview: {
    height: 120,
    overflow: "hidden",
  },
  summaryExpanded: {
    minHeight: 0,
  },
  summaryInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDDDDD",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    color: "#333333",
    minHeight: 600,
    textAlignVertical: "top",
  },
  editActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 12,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2F2F2",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  cancelButtonText: {
    fontSize: 14,
    color: "#555555",
    marginLeft: 4,
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0085FF",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  saveButtonText: {
    fontSize: 14,
    color: "#FFFFFF",
    marginLeft: 4,
  },
  disabledButton: {
    opacity: 0.7,
  },
  summaryActionButtons: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 8,
    marginBottom: 8,
  },
  actionButtonText: {
    fontSize: 14,
    color: "#0085FF",
    marginLeft: 4,
  },
});

export default NoteSummary;
